import React, { useEffect, useRef, useState, useCallback } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { Layers } from "lucide-react";
import { COLORS, POLY_LAYER_LABELS, LEVEL_TO_LAYER } from "@/lib/api";

const TAMPICO = { lat: 22.255, lng: -97.865 };
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const LAYER_STYLE = {
  secciones: {
    idProp: "seccion_str", labelProp: "seccion_str", titlePrefix: "Sección",
    subtitle: "Sección electoral (INE)", color: COLORS.guinda, stroke: COLORS.guindaDark,
  },
  areas: {
    idProp: "area_str", labelProp: "area_str", titlePrefix: "Área",
    subtitle: "Área territorial", color: COLORS.areas, stroke: COLORS.areasStroke,
  },
  colonias: {
    idProp: "nombre", labelProp: "nombre", titlePrefix: "",
    subtitle: "Colonia (INE)", color: COLORS.colonias, stroke: COLORS.coloniasStroke,
  },
};

const _iconCache = {};
function markerIcon(layer) {
  if (_iconCache[layer]) return _iconCache[layer];
  const color = layer === "actividades" ? COLORS.actividades
    : layer === "activaciones" ? COLORS.activaciones
    : COLORS.rac;
  const icon = {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 0.95,
    strokeColor: "#ffffff",
    strokeWeight: 1.4,
    scale: 6.5,
  };
  _iconCache[layer] = icon;
  return icon;
}

// Custom cluster renderer in terracota (no blue in any state).
const clusterRenderer = {
  render: ({ count, position }) => {
    const fill = count >= 100 ? "#A5613F" : "#C17A5A";
    const svg = window.btoa(
      `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52">` +
      `<circle cx="26" cy="26" r="24" fill="${fill}" fill-opacity="0.35"/>` +
      `<circle cx="26" cy="26" r="18" fill="${fill}" fill-opacity="0.95"/>` +
      `<circle cx="26" cy="26" r="18" fill="none" stroke="#ffffff" stroke-width="2"/>` +
      `</svg>`
    );
    return new window.google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new window.google.maps.Size(52, 52),
        anchor: new window.google.maps.Point(26, 26),
      },
      label: { text: String(count), color: "#ffffff", fontSize: "12px", fontWeight: "700" },
      zIndex: 1000 + count,
    });
  },
};

function popupHtml(p) {
  const row = (lbl, val) =>
    val ? `<div class="pop-row"><span class="lbl">${lbl}:</span> ${val}</div>` : "";
  return `
    <div class="pop">
      <div class="pop-head">
        <span class="folio">${p.folio || "Sin folio"}</span>
        <span class="badge">${p.estatus || "—"}</span>
      </div>
      <div class="pop-body">
        <div class="pop-title">${p.asunto || "—"}</div>
        ${p.detalle ? `<div class="pop-detalle">${p.detalle}</div>` : ""}
        ${row("Coordinador", p.coordinador)}
        ${row("Sección", p.seccion)}
        ${row("Área", p.area)}
        ${row("Colonia", p.colonia)}
        ${row("Registro", p.fecha_registro)}
        ${row("Atendido", p.fecha_atendido)}
        ${row("Dirección", p.direccion)}
        ${row("Unidad", p.unidad)}
      </div>
    </div>`;
}

/* Unified polygon layer: choropleth by record density, click to select + fit bounds. */
function PolygonLayer({ geojson, cfg, selectedValue, onSelect, onHover, onHoverEnd }) {
  const map = useMap();
  const dataRef = useRef(null);
  const selRef = useRef(selectedValue);
  selRef.current = selectedValue;

  useEffect(() => {
    if (!map || !window.google || !geojson) return;
    const maxCount = Math.max(1, ...geojson.features.map((f) => f.properties.total || 0));
    const data = new window.google.maps.Data({ map });
    data.addGeoJson(geojson, { idPropertyName: cfg.idProp });
    dataRef.current = data;

    const styleFn = (feature) => {
      const val = String(feature.getProperty(cfg.idProp));
      const total = feature.getProperty("total") || 0;
      const isSel = selRef.current != null &&
        String(selRef.current).trim().toUpperCase() === val.trim().toUpperCase();
      const ratio = total / maxCount;
      return {
        fillColor: cfg.color,
        fillOpacity: isSel ? 0.55 : 0.08 + 0.42 * ratio,
        strokeColor: isSel ? cfg.stroke : cfg.color,
        strokeWeight: isSel ? 3.5 : 1.2,
        strokeOpacity: isSel ? 1 : 0.6,
        zIndex: isSel ? 40 : 12,
      };
    };
    data.setStyle(styleFn);
    dataRef.current._styleFn = styleFn;

    const buildTip = (feature, clickHint) => ({
      kind: cfg.titlePrefix || "colonia",
      title: `${cfg.titlePrefix ? cfg.titlePrefix + " " : ""}${feature.getProperty(cfg.labelProp)}`,
      subtitle: cfg.subtitle,
      total: feature.getProperty("total") || 0,
      act: feature.getProperty("act") || 0,
      rac: feature.getProperty("rac") || 0,
      activ: feature.getProperty("activ") || 0,
      resultado_2024: feature.getProperty("resultado_2024") || "",
      lista_nominal: feature.getProperty("lista_nominal") || "",
      color: cfg.stroke,
      clickHint,
    });

    const clickL = data.addListener("click", (e) => {
      const val = String(e.feature.getProperty(cfg.idProp));
      const b = new window.google.maps.LatLngBounds();
      e.feature.getGeometry().forEachLatLng((ll) => b.extend(ll));
      if (!b.isEmpty()) map.fitBounds(b, 40);
      onSelect(val);
    });
    const overL = data.addListener("mouseover", (e) => {
      data.overrideStyle(e.feature, { fillOpacity: 0.62, strokeWeight: 2.6 });
      onHover(buildTip(e.feature, true));
    });
    const outL = data.addListener("mouseout", () => {
      data.revertStyle();
      onHoverEnd();
    });

    return () => {
      window.google.maps.event.removeListener(clickL);
      window.google.maps.event.removeListener(overL);
      window.google.maps.event.removeListener(outL);
      onHoverEnd();
      data.setMap(null);
    };
  }, [map, geojson, cfg, onSelect, onHover, onHoverEnd]);

  useEffect(() => {
    const data = dataRef.current;
    if (data && data._styleFn) {
      data.revertStyle();
      data.setStyle(data._styleFn);
    }
  }, [selectedValue]);

  return null;
}

/* Individual point markers, rendered when a polygon is selected. */
function PointsLayer({ points }) {
  const map = useMap();
  const clustererRef = useRef(null);
  const infoRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google) return;
    if (!infoRef.current) infoRef.current = new window.google.maps.InfoWindow();
    if (!clustererRef.current) clustererRef.current = new MarkerClusterer({
      map,
      renderer: clusterRenderer,
      algorithm: new SuperClusterAlgorithm({ radius: 200, maxZoom: 16, minPoints: 2 }),
    });

    const markers = points.map((p) => {
      const m = new window.google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        icon: markerIcon(p.layer),
      });
      m.addListener("click", () => {
        infoRef.current.setContent(popupHtml(p));
        infoRef.current.open(map, m);
      });
      return m;
    });

    clustererRef.current.clearMarkers();
    clustererRef.current.addMarkers(markers);

    return () => {
      if (clustererRef.current) clustererRef.current.clearMarkers();
      if (infoRef.current) infoRef.current.close();
    };
  }, [map, points]);

  return null;
}

/* Highlight the currently selected polygon from its own layer, so the selection
   stays visible even when a different polygon layer is toggled on. */
function SelectionOverlay({ geojson, idProp, value, color, stroke }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google || !geojson || value == null || value === "") return;
    const target = String(value).trim().toUpperCase();
    const feature = (geojson.features || []).find(
      (f) => String(f.properties?.[idProp] ?? "").trim().toUpperCase() === target
    );
    if (!feature) return;
    const data = new window.google.maps.Data({ map });
    data.addGeoJson({ type: "FeatureCollection", features: [feature] });
    data.setStyle({
      fillColor: color, fillOpacity: 0.5,
      strokeColor: stroke, strokeWeight: 3.5, strokeOpacity: 1, zIndex: 50,
    });
    return () => data.setMap(null);
  }, [map, geojson, idProp, value, color, stroke]);
  return null;
}

function MapController({ points, fitSignature }) {
  const map = useMap();
  const pendingFitRef = useRef(false);

  useEffect(() => {
    if (!map || !window.google) return;
    const nudges = [250, 800, 1600, 2800].map((d) =>
      setTimeout(() => {
        window.google.maps.event.trigger(map, "resize");
        window.dispatchEvent(new Event("resize"));
      }, d)
    );
    return () => nudges.forEach(clearTimeout);
  }, [map]);

  // A narrowing action (polygon selection or committed search) requests a refit.
  // Switching the polygon layer type does NOT change the signature, so the
  // current zoom/position is preserved.
  useEffect(() => {
    pendingFitRef.current = true;
  }, [fitSignature]);

  useEffect(() => {
    if (!map || !window.google) return;
    if (!pendingFitRef.current) return;
    if (!points.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => {
      if (p.lat != null && p.lng != null) bounds.extend({ lat: p.lat, lng: p.lng });
    });
    if (bounds.isEmpty()) return;
    pendingFitRef.current = false;
    window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
      if (map.getZoom() > 16) map.setZoom(16);
    });
    map.fitBounds(bounds, 60);
  }, [map, points]);

  return null;
}

/* Overlay panel: single-selection layer switcher (only admin-enabled layers). */
function LayerTogglePanel({ available, activeLayer, onSelect }) {
  const keys = ["secciones", "areas", "colonias"].filter((k) => available?.[k]);
  if (!keys.length) return null;
  const dot = { secciones: COLORS.guinda, areas: COLORS.areas, colonias: COLORS.colonias };
  return (
    <div
      data-testid="layer-toggle-panel"
      className="absolute top-3 right-3 z-40 bg-white/95 backdrop-blur rounded-lg border border-slate-200 shadow-md px-3 py-2.5 w-[178px]"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Layers className="w-3.5 h-3.5 text-guinda" />
        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Capa activa</span>
      </div>
      <div className="space-y-1">
        {keys.map((k) => {
          const on = activeLayer === k;
          return (
            <button
              key={k}
              data-testid={`poly-toggle-${k}`}
              onClick={() => onSelect(k)}
              className={`w-full flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs transition-colors ${
                on ? "bg-slate-50" : "opacity-55 hover:opacity-100"
              }`}
            >
              <span className="flex items-center gap-2 text-slate-700">
                <span className="w-3 h-3 rounded-sm border" style={{ background: on ? dot[k] : "transparent", borderColor: dot[k] }} />
                {POLY_LAYER_LABELS[k]}
              </span>
              <span className={`relative inline-block w-7 h-4 rounded-full transition-colors ${on ? "bg-guinda" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${on ? "left-3.5" : "left-0.5"}`} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MapView({
  points, geojson, areasGeojson, coloniasGeojson,
  activeLayer, availableLayers, onSelectLayer,
  selectedValue, onSelectPolygon, appliedSearch, selectedLevel,
}) {
  const [error, setError] = useState(false);
  const [tip, setTip] = useState(null);
  const posRef = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    posRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        setPos(posRef.current);
        rafRef.current = null;
      });
    }
  };

  const onHover = useCallback((d) => setTip(d), []);
  const onHoverEnd = useCallback(() => setTip(null), []);

  if (!API_KEY) {
    return (
      <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm p-6 text-center">
        API Key de Google Maps no configurada.
      </div>
    );
  }

  const activeGeojson =
    activeLayer === "secciones" ? geojson :
    activeLayer === "areas" ? areasGeojson :
    activeLayer === "colonias" ? coloniasGeojson : null;
  const cfg = LAYER_STYLE[activeLayer];
  const headerColor = tip?.color || COLORS.guinda;
  const fitSignature = `${selectedValue ?? ""}|${appliedSearch ?? ""}`;

  // Overlay the selected polygon from its own layer when a different layer is active.
  const selLayerKey = selectedLevel ? LEVEL_TO_LAYER[selectedLevel] : null;
  const selGeojson =
    selLayerKey === "secciones" ? geojson :
    selLayerKey === "areas" ? areasGeojson :
    selLayerKey === "colonias" ? coloniasGeojson : null;
  const selCfg = selLayerKey ? LAYER_STYLE[selLayerKey] : null;
  const showSelectionOverlay =
    selLayerKey && selLayerKey !== activeLayer && selGeojson && selCfg && selectedValue;

  return (
    <div className="w-full h-full relative" data-testid="map-view" onMouseMove={handleMove}>
      <APIProvider apiKey={API_KEY} onError={() => setError(true)}>
        {error ? (
          <div className="w-full h-full flex items-center justify-center text-red-600 text-sm p-6 text-center">
            No se pudo cargar Google Maps. Habilita la <b>&nbsp;Maps JavaScript API&nbsp;</b> para esta API Key.
          </div>
        ) : (
          <Map
            style={{ width: "100%", height: "100%" }}
            defaultCenter={TAMPICO}
            defaultZoom={10}
            gestureHandling="greedy"
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={true}
            zoomControl={true}
            clickableIcons={false}
          >
            {activeLayer && cfg && activeGeojson && (
              <PolygonLayer geojson={activeGeojson} cfg={cfg}
                selectedValue={selectedValue} onSelect={onSelectPolygon}
                onHover={onHover} onHoverEnd={onHoverEnd} />
            )}
            {points.length > 0 && <PointsLayer points={points} />}
            {showSelectionOverlay && (
              <SelectionOverlay geojson={selGeojson} idProp={selCfg.idProp}
                value={selectedValue} color={selCfg.color} stroke={selCfg.stroke} />
            )}
            <MapController points={points} fitSignature={fitSignature} />
          </Map>
        )}

        <LayerTogglePanel available={availableLayers} activeLayer={activeLayer} onSelect={onSelectLayer} />

        {tip && (
          <div
            data-testid="section-tooltip"
            className="absolute z-50 pointer-events-none"
            style={{ left: pos.x, top: pos.y, transform: "translate(-50%, calc(-100% - 16px))" }}
          >
            <div className="min-w-[168px] rounded-xl bg-white shadow-[0_10px_30px_rgba(15,23,42,0.22)] ring-1 ring-black/5 overflow-hidden">
              <div className="px-3.5 py-2" style={{ background: headerColor }}>
                <div className="text-white font-heading font-bold text-sm leading-tight">{tip.title}</div>
                <div className="text-white/80 text-[10px]">{tip.subtitle}</div>
                {(tip.resultado_2024 || tip.lista_nominal) && (
                  <div className="mt-1.5 pt-1.5 border-t border-white/20 space-y-0.5" data-testid="tooltip-cseccion">
                    {tip.resultado_2024 && (
                      <div className="flex items-center justify-between gap-3 text-[10px]">
                        <span className="text-white/70">Resultado 2024</span>
                        <span className="text-white font-semibold">{tip.resultado_2024}</span>
                      </div>
                    )}
                    {tip.lista_nominal && (
                      <div className="flex items-center justify-between gap-3 text-[10px]">
                        <span className="text-white/70">Lista nominal</span>
                        <span className="text-white font-semibold">{Number(tip.lista_nominal).toLocaleString("es-MX")}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="px-3.5 py-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Total</span>
                  <span className="font-heading font-extrabold text-slate-900 text-base leading-none">
                    {tip.total.toLocaleString("es-MX")}
                  </span>
                </div>
                <div className="h-px bg-slate-100" />
                <div className="flex items-center justify-between text-[12px]">
                  <span className="flex items-center gap-1.5 text-slate-600">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.actividades }} />
                    Obras
                  </span>
                  <span className="font-bold text-slate-800">{tip.act.toLocaleString("es-MX")}</span>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="flex items-center gap-1.5 text-slate-600">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.rac }} />
                    Reportes
                  </span>
                  <span className="font-bold text-slate-800">{tip.rac.toLocaleString("es-MX")}</span>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="flex items-center gap-1.5 text-slate-600">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.activaciones }} />
                    Activaciones
                  </span>
                  <span className="font-bold text-slate-800">{(tip.activ || 0).toLocaleString("es-MX")}</span>
                </div>
              </div>
              {tip.clickHint && (
                <div className="px-3.5 pb-2 -mt-0.5">
                  <div className="text-[10px] font-semibold" style={{ color: headerColor }}>Clic para ver detalle →</div>
                </div>
              )}
            </div>
            <div className="w-3 h-3 bg-white ring-1 ring-black/5 rotate-45 mx-auto -mt-1.5" />
          </div>
        )}
      </APIProvider>
    </div>
  );
}
